// User Profile Service - Manages user profile data
import { authService } from './auth';

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  photoUrl?: string;
  websiteUrl?: string;
  twitterUrl?: string;
  linkedinUrl?: string;
  githubUrl?: string;
  instagramUrl?: string;
  youtubeUrl?: string;
  facebookUrl?: string;
  tiktokUrl?: string;
  phoneNumber?: string;
  jobTitle?: string;
  company?: string;
  bio?: string;
  createdAt: string;
  updatedAt: string;
  authProvider?: 'email' | 'google' | 'demo';
}

class UserProfileService {
  async getCurrentUser(): Promise<UserProfile | null> {
    try {
      // Get user from authService (backend)
      const authUser = authService.getCurrentUser();
      if (!authUser) return null;

      // Map AuthUser to UserProfile
      return {
        id: authUser.id,
        email: authUser.email,
        firstName: authUser.firstName || '',
        lastName: authUser.lastName || '',
        displayName: authUser.displayName || '',
        photoUrl: authUser.photoUrl || undefined,
        bio: authUser.bio || undefined,
        websiteUrl: authUser.websiteUrl || undefined,
        twitterUrl: authUser.twitterUrl || undefined,
        linkedinUrl: authUser.linkedinUrl || undefined,
        githubUrl: authUser.githubUrl || undefined,
        instagramUrl: authUser.instagramUrl || undefined,
        youtubeUrl: authUser.youtubeUrl || undefined,
        facebookUrl: authUser.facebookUrl || undefined,
        tiktokUrl: authUser.tiktokUrl || undefined,
        phoneNumber: authUser.phoneNumber || undefined,
        jobTitle: authUser.jobTitle || undefined,
        company: authUser.company || undefined,
        createdAt: authUser.createdAt,
        updatedAt: authUser.createdAt,
        authProvider: authUser.hasGoogleAuth ? 'google' : 'email'
      };
    } catch (error) {
      console.error('Error loading user profile:', error);
      return null;
    }
  }

  async saveUserProfile(profile: UserProfile): Promise<void> {
    // Profile is now managed by backend via authService
    console.log('Profile updates should be done through authService.updateProfile()');
  }

  async createDefaultProfile(email: string, authProvider: 'email' | 'google' | 'demo' = 'demo'): Promise<UserProfile> {
    // Profile is created automatically by backend during registration
    const authUser = authService.getCurrentUser();
    if (!authUser) {
      throw new Error('No authenticated user found');
    }

    return {
      id: authUser.id,
      email: authUser.email,
      firstName: authUser.firstName || '',
      lastName: authUser.lastName || '',
      displayName: authUser.displayName || '',
      createdAt: authUser.createdAt,
      updatedAt: authUser.createdAt,
      authProvider
    };
  }

  async updateProfile(updates: Partial<UserProfile>): Promise<UserProfile> {
    // Use authService to update profile on backend
    await authService.updateProfile({
      firstName: updates.firstName,
      lastName: updates.lastName,
      email: updates.email,
      displayName: updates.displayName,
      bio: updates.bio,
      photoUrl: updates.photoUrl,
      websiteUrl: updates.websiteUrl,
      twitterUrl: updates.twitterUrl,
      linkedinUrl: updates.linkedinUrl,
      githubUrl: updates.githubUrl,
      instagramUrl: updates.instagramUrl,
      youtubeUrl: updates.youtubeUrl,
      facebookUrl: updates.facebookUrl,
      tiktokUrl: updates.tiktokUrl,
      phoneNumber: updates.phoneNumber,
      jobTitle: updates.jobTitle,
      company: updates.company,
    });

    const currentProfile = await this.getCurrentUser();
    if (!currentProfile) {
      throw new Error('No user profile found');
    }

    return currentProfile;
  }

  async populateFromGoogleAuth(googleUser: any): Promise<UserProfile> {
    // Profile is created automatically by backend during Google OAuth
    const authUser = authService.getCurrentUser();
    if (!authUser) {
      throw new Error('No authenticated user found');
    }

    return {
      id: authUser.id,
      email: authUser.email,
      firstName: authUser.firstName || '',
      lastName: authUser.lastName || '',
      displayName: authUser.displayName || '',
      photoUrl: googleUser.picture || undefined,
      createdAt: authUser.createdAt,
      updatedAt: authUser.createdAt,
      authProvider: 'google'
    };
  }

  async deleteProfile(): Promise<void> {
    // Profile deletion is handled by authService.deleteAccount()
    await authService.deleteAccount();
  }
}

export const userProfileService = new UserProfileService();
